/* eslint-disable */
<!-- prettier-ignore-start -->
<template>
  <div class="gmv-stats">
    <!-- Live GMV Controls -->
    <div class="live-controls" v-if="showControls">
      <div class="control-group">
        <button
          @click="toggleLiveMode"
          :class="['live-btn', { active: isLiveMode }]"
          :disabled="liveStatus.isRunning && !isLiveMode"
        >
          {{ isLiveMode ? '🔴 LIVE' : '▶️ Start Live' }}
        </button>

        <div class="interval-controls" v-if="isLiveMode">
          <label>Update Interval:</label>
          <select @change="updateInterval" v-model="selectedInterval">
            <option value="5000">5 seconds</option>
            <option value="10000">10 seconds</option>
            <option value="60000">1 minute</option>
            <option value="300000">5 minutes</option>
            <option value="3600000">1 hour</option>
            <option value="86400000">24 hours</option>
          </select>
        </div>
      </div>

      <div class="live-status" v-if="isLiveMode">
        <p>
          <span v-if="liveStatus.isExtrapolating" class="extrapolating">
            📈 Extrapolating to {{ formatCurrency(liveStatus.extrapolatedGMV) }}
          </span>
          <span v-else class="reached-target">🎯 Target reached</span>
        </p>
        <p class="status-detail">
          Last updated: {{ formatTime(liveStatus.lastFetchTime) }}
        </p>
      </div>
    </div>

    <div class="stats-grid">
      <!-- Latest GMV Card -->
      <div class="stat-card primary">
        <div class="stat-content">
          <div class="stat-value-container">
            <NumberFlow
              :value="latestGMV"
              :format="{
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }"
              :plugins="isIPhone ? [] : [continuous]"
              :trend="isIPhone ? 0 : 1"
              class="stat-value animated-value safari-compatible"
              :transformTiming="isIPhone ? { duration: 2000, easing: 'linear' } : { duration: 1000, easing: 'ease-in-out' }"
              :opacityTiming="isIPhone ? { duration: 1000, easing: 'linear' } : { duration: 600, easing: 'ease-in-out' }"
              :willChange="!isIPhone"
              :respectMotionPreference="false"
            />
            <h2> Made for Creators </h2>
            <div v-if="isLiveMode" class="live-indicator">
              <span :class="['live-dot', { pulsing: liveStatus.isExtrapolating }]"></span>
              {{ liveStatus.isExtrapolating ? 'Active' : 'Live Data' }}
            </div>
          </div>
          <p class="stat-date">{{ latestDate }}</p>
        </div>
      </div>

      <!-- Growth Rate Card -->
      <div class = 'row'>
      <div class="stat-card">
        <div class="stat-icon growth">
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8">
            <path
              d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"
            />
          </svg>
        </div>
        <div class="stat-content">
          <h3 class="stat-label">30-Day Growth</h3>
          <p class="stat-value" :class="growthClass">{{ growthPercentage }}%</p>
          <p class="stat-description">{{ growthDescription }}</p>
        </div>
      </div>

      <!-- Average GMV Card -->
      <div class="stat-card">
        <div class="stat-icon average">
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        </div>

        <div class="stat-content">
          <h3 class="stat-label">30-Day Average</h3>
          <p class="stat-value">{{ formatCurrency(averageGMV) }}</p>
          <p class="stat-description">Daily average</p>
        </div>
      </div>

      <!-- Total Records Card -->
      <div class="stat-card">
        <div class="stat-icon records">
          <svg viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8">
            <path
              d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"
            />
          </svg>
        </div>
        <div class="stat-content">
          <h3 class="stat-label">Data Points</h3>
          <p class="stat-value">{{ totalRecords }}</p>
          <p class="stat-description">Days tracked</p>
        </div>
      </div>

    </div>
  </div>
  </div>
</template>

<script>
import NumberFlow, { continuous } from '@number-flow/vue'
import LiveGMVService from '@/services/liveGMVService.js'

export default {
  name: "GMVStats",
  components: {
    NumberFlow,
  },
  props: {
    gmvData: {
      type: Array,
      required: true,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      continuous,
      liveGMV: 0,
      isLiveMode: false,
      liveStatus: {},
      unsubscribe: null,
      selectedInterval: '86400000', // Default 10 seconds for demo
      isIPhone: false,
    };
  },
  computed: {
    latestGMV() {
      // Use live GMV if in live mode and we have received data from the service
      if (this.isLiveMode && this.liveStatus && this.liveStatus.displayedGMV !== undefined) {
        return this.liveStatus.displayedGMV;
      }

      // Fallback to static data
      if (!this.gmvData || this.gmvData.length === 0 || !this.gmvData[this.gmvData.length - 1].TOTAL_GMV_LIFETIME) return 0;
      return this.gmvData[this.gmvData.length - 1].TOTAL_GMV_LIFETIME;
    },
    latestDate() {
      if (!this.gmvData || this.gmvData.length === 0) return "";
      const date = new Date(this.gmvData[this.gmvData.length - 1].date);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    averageGMV() {
      if (!this.gmvData || this.gmvData.length === 0) return 0;

      // Calculate average for last 30 days (or all data if less than 30 days)
      const last30Days = this.gmvData.slice(-30);
      const sum = last30Days.reduce(
        (acc, item) => acc + item.TOTAL_GMV_LIFETIME,
        0
      );
      return Math.round(sum / last30Days.length);
    },
    growthPercentage() {
      if (!this.gmvData || this.gmvData.length < 2) return 0;

      // Calculate 30-day growth (or use available data if less than 30 days)
      const currentIndex = this.gmvData.length - 1;
      const thirtyDaysAgoIndex = Math.max(0, currentIndex - 30);

      const latest = this.gmvData[currentIndex].TOTAL_GMV_LIFETIME;
      const thirtyDaysAgo = this.gmvData[thirtyDaysAgoIndex].TOTAL_GMV_LIFETIME;

      const growth = ((latest - thirtyDaysAgo) / thirtyDaysAgo) * 100;
      return growth.toFixed(1);
    },
    growthClass() {
      const growth = parseFloat(this.growthPercentage);
      if (growth > 0) return "positive";
      if (growth < 0) return "negative";
      return "neutral";
    },
    growthDescription() {
      const growth = parseFloat(this.growthPercentage);
      if (growth > 0) return "Upward trend";
      if (growth < 0) return "Downward trend";
      return "Stable";
    },
    totalRecords() {
      return this.gmvData ? this.gmvData.length : 0;
    },
  },
  methods: {
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    },

    formatTime(date) {
      if (!date) return 'Never';
      return new Date(date).toLocaleTimeString();
    },

    async toggleLiveMode() {
      if (this.isLiveMode) {
        // Stop live mode
        this.stopLiveMode();
      } else {
        // Start live mode
        await this.startLiveMode();
      }
    },

    async startLiveMode() {
      try {
        console.log('🚀 Starting live GMV mode...');

        // Set the update interval
        LiveGMVService.setUpdateInterval(parseInt(this.selectedInterval));

        // Subscribe to updates first
        this.unsubscribe = LiveGMVService.subscribe((status) => {
          // console.log('🔄 Received live GMV update:', status);
          this.liveGMV = status.displayedGMV;
          this.liveStatus = status;

          /*
          console.log('📊 Updated component state:', {
            liveGMV: this.liveGMV,
            latestGMV: this.latestGMV,
            isLiveMode: this.isLiveMode,
            displayedGMV: status.displayedGMV
          });
          */
        });

        // Start the service
        await LiveGMVService.start();

        this.isLiveMode = true;
        console.log('✅ Live GMV mode started successfully');

        // Get initial status to ensure we have data
        const initialStatus = LiveGMVService.getStatus();
        if (initialStatus.displayedGMV > 0) {
          this.liveGMV = initialStatus.displayedGMV;
          this.liveStatus = initialStatus;
          console.log('📋 Set initial status:', initialStatus);
        }

      } catch (error) {
        console.error('❌ Failed to start live mode:', error);
        this.isLiveMode = false;

        // Clean up subscription if it was created
        if (this.unsubscribe) {
          this.unsubscribe();
          this.unsubscribe = null;
        }

        // Don't show alert in mounted() context, just log the error
        if (this.$el && this.$el.parentNode) {
          console.log('Failed to start live mode: ' + error.message);
        }
      }
    },

    stopLiveMode() {
      console.log('🛑 Stopping live GMV mode...');

      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }

      LiveGMVService.stop();
      this.isLiveMode = false;
      this.liveGMV = 0;
      this.liveStatus = {};

      //console.log('✅ Live GMV mode stopped');
    },


    updateInterval() {
      if (this.isLiveMode) {
        LiveGMVService.setUpdateInterval(parseInt(this.selectedInterval));
       // console.log(`🔄 Updated interval to ${this.selectedInterval}ms`);
      }
    },
  },

  watch: {
    // Watch for changes in live status to ensure reactivity
    liveStatus: {
      handler(newStatus) {
        if (newStatus && newStatus.displayedGMV !== undefined) {
         // console.log('👀 Live status changed, new GMV:', newStatus.displayedGMV);
        }
      },
      deep: true
    },

    // Watch the computed latestGMV to debug when it changes
    latestGMV: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          //console.log('💰 latestGMV changed:', { from: oldValue, to: newValue, isLiveMode: this.isLiveMode });
        }
      }
    }
  },

  mounted() {
    // Detect iPhone Safari specifically
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    this.isIPhone = /iPhone/.test(userAgent) && /Safari/.test(userAgent) && !/CriOS/.test(userAgent) && !/FxiOS/.test(userAgent);

    console.log('📱 Device detection:', {
      userAgent,
      isIPhone: this.isIPhone,
      viewport: `${window.innerWidth}x${window.innerHeight}`
    });

    this.startLiveMode()
  },

  beforeUnmount() {
    // Clean up when component is destroyed
    this.stopLiveMode();
  },
};
</script>

<style lang="scss" scoped>

@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&family=DM+Serif+Display:ital@0;1&family=EB+Garamond:ital,wght@0,400..800;1,400..800&family=Geist:wght@100..900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Lora:ital,wght@0,400..700;1,400..700&family=Newsreader:ital,opsz,wght@0,6..72,200..800;1,6..72,200..800&family=Playfair+Display:ital,wght@0,400..900;1,400..900&family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');

*{
  font-family: 'Inter', sans-serif;
}

.gmv-stats {
  margin-bottom: 2rem;
}

.live-controls {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  color: white;
  display: none;

  .control-group {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }

  .live-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid rgba(255, 255, 255, 0.3);
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }

    &.active {
      background: #ef4444;
      border-color: #ef4444;
      animation: pulse 2s infinite;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .interval-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    label {
      font-size: 0.875rem;
      font-weight: 500;
    }

    select {
      background: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: #333;
      padding: 0.5rem;
      border-radius: 6px;
      font-size: 0.875rem;
    }
  }

  .live-status {
    p {
      margin: 0.25rem 0;
      font-size: 0.875rem;
    }

    .extrapolating {
      color: #fbbf24;
      font-weight: 600;
    }

    .reached-target {
      color: #10b981;
      font-weight: 600;
    }

    .status-detail {
      opacity: 0.8;
    }
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.row {
  display: none;
}

:global(number-flow-vue) {
  --number-flow-char-height: 0.85em;
  font-size: 80px !important;
  font-family: 'Inter', sans-serif !important;
  letter-spacing: -2px !important;
  font-weight: 700 !important;
  color: #0f1955 !important;

  /* Universal Safari animation fixes */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: transform, opacity;
  -webkit-perspective: 1000px;
  perspective: 1000px;

  @media screen and (max-width: 768px) {
    font-size: 40px !important;
    letter-spacing: -1.5px !important;

    /* iPhone-specific performance optimizations */
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
    -webkit-transform-style: preserve-3d !important;
    transform-style: preserve-3d !important;
    position: relative;
    z-index: 1;
  }
}

/* iPhone Safari specific fixes */
@media screen and (max-width: 428px) and (-webkit-min-device-pixel-ratio: 2) {
  :global(number-flow-vue) {
    /* Force hardware acceleration on iPhone */
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
    -webkit-transform-style: preserve-3d !important;
    transform-style: preserve-3d !important;
    -webkit-backface-visibility: hidden !important;
    backface-visibility: hidden !important;

    /* Reduce complexity for iPhone performance */
    --number-flow-char-height: 1em !important;
    font-size: 32px !important;
    letter-spacing: -1px !important;

    /* Force compositing layer */
    position: relative;
    z-index: 1;
    isolation: isolate;
  }

  .safari-compatible {
    -webkit-transform: translate3d(0, 0, 0) !important;
    transform: translate3d(0, 0, 0) !important;
    -webkit-transform-style: preserve-3d !important;
    transform-style: preserve-3d !important;
    position: relative;
    z-index: 1;
  }
}

/* Safari-specific animation optimizations */
.safari-compatible {
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  will-change: transform, opacity;
  -webkit-perspective: 1000px;
  perspective: 1000px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Force hardware acceleration on mobile Safari */
@supports (-webkit-overflow-scrolling: touch) {
  :global(number-flow-vue) {
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    -webkit-transform-style: preserve-3d;
    transform-style: preserve-3d;
  }

  .safari-compatible {
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    -webkit-transform-style: preserve-3d;
    transform-style: preserve-3d;
  }
}

.stat-date{
  display: none;
}

.stat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0px;
}

.stat-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0 0 0.5rem 0;
  opacity: 0.8;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  line-height: 1.2;

  &.positive {
    color: #22c55e;
  }

  &.negative {
    color: #ef4444;
  }

  &.neutral {
    color: #6b7280;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
}

.stat-value-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0px;

  h2{
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 500;
    letter-spacing: -.36px;
    margin: 0;
    color: #808EB6;

    @media (max-width: 800px) {
      font-size: 14px;
    }
  }
}

.live-indicator {
  display: none;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.2px;
  color: #030025;
  margin-top: 24px;
  background: rgba(#10b981, .2);
  padding: 8px 12px;
  border-radius: 18px;

  .live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #10b981;

    &.pulsing {
      background: #10b981;
      animation: pulse 1.5s infinite;
    }
  }
}

.animated-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  color: inherit;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
}

.stat-date,
.stat-description {
  font-size: 0.75rem;
  margin: 0;
  opacity: 0.7;
}

.w-8 {
  width: 2rem;
}

.h-8 {
  height: 2rem;
}

@keyframes pulse {
  0%, 100% {
    opacity: .7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
</style>
<!-- prettier-ignore-end -->
